// Логика редактора идеи — одна на кабинет продавца (Next.js) и консоль
// (Bun SPA). Разметка у приложений своя (разные темы и UI-киты), а всё, что
// решает «что станет с фото, метками и суммой», живёт здесь, чтобы поведение
// двух редакторов не разъехалось. Без React: чистые функции над данными.

import { clampPercent, type PinPoint } from "./pins"

export const IDEA_EDITOR_MAX_IMAGES = 4
export const IDEA_EDITOR_TITLE_MAX = 100
export const IDEA_EDITOR_DESCRIPTION_MAX = 500
/** «JPEG, PNG до 10 Мб». */
export const IDEA_EDITOR_MAX_FILE_BYTES = 10 * 1024 * 1024
export const IDEA_EDITOR_ACCEPT = "image/jpeg,image/png"

export const IDEA_EDITOR_AUDIENCES = ["women", "men", "kids", "home"] as const
export type IdeaEditorAudience = (typeof IDEA_EDITOR_AUDIENCES)[number]

export const IDEA_EDITOR_TYPES = ["inspiration", "seasonal", "collaboration"] as const
export type IdeaEditorType = (typeof IDEA_EDITOR_TYPES)[number]

export type IdeaEditorPin = PinPoint & { id: string; productId: string }
export type IdeaEditorImage = { id: string; url: string; pins: IdeaEditorPin[] }

export type IdeaEditorDiscount = { value: number; unit: "rub" | "percent" }

const uid = (): string => globalThis.crypto.randomUUID()
const round1 = (n: number): number => Math.round(n * 10) / 10

/** Проверка файла до загрузки: тип и размер из подписи под кнопкой. */
export const ideaFileProblem = (file: { type: string; size: number }): "type" | "size" | null => {
  if (file.type !== "image/jpeg" && file.type !== "image/png") return "type"
  if (file.size > IDEA_EDITOR_MAX_FILE_BYTES) return "size"
  return null
}

/** Добавить загруженные фото в свободные слоты (не больше четырёх). */
export const appendImages = (images: IdeaEditorImage[], urls: string[]): IdeaEditorImage[] => {
  const room = IDEA_EDITOR_MAX_IMAGES - images.length
  if (room <= 0) return images
  return [...images, ...urls.slice(0, room).map((url) => ({ id: uid(), url, pins: [] }))]
}

/** Сделать фото главным — оно встаёт первым; остальные сохраняют порядок. */
export const setMainImage = (images: IdeaEditorImage[], id: string): IdeaEditorImage[] => {
  const target = images.find((i) => i.id === id)
  if (!target || images[0]?.id === id) return images
  return [target, ...images.filter((i) => i.id !== id)]
}

/** Заменить фото: метки сбрасываются — на новом кадре они не совпадут с товарами. */
export const replaceImage = (
  images: IdeaEditorImage[],
  id: string,
  url: string,
): IdeaEditorImage[] => images.map((i) => (i.id === id ? { ...i, url, pins: [] } : i))

export const removeImage = (images: IdeaEditorImage[], id: string): IdeaEditorImage[] =>
  images.filter((i) => i.id !== id)

// Куда встают новые метки, если товаров добавили сразу несколько: веером по
// кадру, чтобы не лечь друг на друга; дальше продавец перетаскивает каждую.
const SPOTS: readonly PinPoint[] = [
  { x: 30, y: 30 },
  { x: 60, y: 30 },
  { x: 45, y: 50 },
  { x: 30, y: 70 },
  { x: 60, y: 70 },
  { x: 75, y: 50 },
  { x: 15, y: 50 },
  { x: 50, y: 15 },
  { x: 50, y: 85 },
]

/**
 * Применить выбор товаров из модалки к меткам фото: оставить метки выбранных,
 * снять метки невыбранных, добавить метки новым. Первая новая метка встаёт в
 * точку клика по фото (`at`), остальные — веером.
 */
export const applyProductSelection = (
  pins: IdeaEditorPin[],
  productIds: string[],
  at?: PinPoint,
): IdeaEditorPin[] => {
  const keep = pins.filter((p) => productIds.includes(p.productId))
  const existing = new Set(keep.map((p) => p.productId))
  const added = productIds
    .filter((id) => !existing.has(id))
    .map((productId, j) => {
      const spot =
        j === 0 && at ? at : (SPOTS[(keep.length + j) % SPOTS.length] ?? { x: 50, y: 50 })
      return { id: uid(), productId, x: round1(clampPercent(spot.x)), y: round1(clampPercent(spot.y)) }
    })
  return [...keep, ...added]
}

export const movePin = (pins: IdeaEditorPin[], pinId: string, point: PinPoint): IdeaEditorPin[] =>
  pins.map((p) =>
    p.id === pinId ? { ...p, x: round1(clampPercent(point.x)), y: round1(clampPercent(point.y)) } : p,
  )

export const updateImagePins = (
  images: IdeaEditorImage[],
  imageId: string,
  update: (pins: IdeaEditorPin[]) => IdeaEditorPin[],
): IdeaEditorImage[] => images.map((i) => (i.id === imageId ? { ...i, pins: update(i.pins) } : i))

/** Убрать товар из идеи — его метки снимаются со всех фото. */
export const removeProduct = (images: IdeaEditorImage[], productId: string): IdeaEditorImage[] =>
  images.map((i) => ({ ...i, pins: i.pins.filter((p) => p.productId !== productId) }))

/** Различные товары идеи в порядке меток: сначала главное фото, потом остальные. */
export const ideaProductIds = (images: IdeaEditorImage[]): string[] =>
  Array.from(new Set(images.flatMap((i) => i.pins.map((p) => p.productId))))

/** Итого со скидкой на группу; не ниже нуля. Так же считает шлюз. */
export const applyGroupDiscount = (total: number, discount: IdeaEditorDiscount | null): number => {
  if (!discount || !(discount.value > 0)) return total
  const off =
    discount.unit === "percent" ? Math.round((total * discount.value) / 100) : discount.value
  return Math.max(0, total - off)
}

/** Черновик скидки из поля ввода: пусто/ноль — без скидки, процент — до 99. */
export const parseDiscountInput = (
  raw: string,
  unit: IdeaEditorDiscount["unit"],
): IdeaEditorDiscount | null => {
  const value = Number(raw.replace(/\s/g, "").replace(",", "."))
  if (!Number.isFinite(value) || value <= 0) return null
  return { value: unit === "percent" ? Math.min(99, value) : value, unit }
}

/** Бейдж «-24%» у товара со старой ценой. */
export const discountPercent = (price: number, oldPrice?: number | null): number | null => {
  if (!oldPrice || oldPrice <= price) return null
  return Math.round(((oldPrice - price) / oldPrice) * 100)
}

/** Фильтры списка идей: «Все идеи / Не опубликованные / Заблокированные». */
export type IdeaListFilter = "all" | "unpublished" | "blocked"
export const IDEA_LIST_FILTERS: readonly IdeaListFilter[] = ["all", "unpublished", "blocked"]

export const matchesIdeaFilter = (status: string, filter: IdeaListFilter): boolean => {
  if (status === "archived") return false
  if (filter === "blocked") return status === "blocked"
  if (filter === "unpublished") return status !== "published" && status !== "blocked"
  return true
}

/** Страница идеи на витрине: подборка ленты со slug `idea-<id>`. */
export const storefrontIdeaPath = (ideaId: string): string =>
  `/feed/${encodeURIComponent(`idea-${ideaId}`)}`
export const storefrontProductPath = (productId: string): string =>
  `/product/${encodeURIComponent(productId)}`

/**
 * Адрес витрины из адреса консоли/кабинета: seller.osmix.X и admin.osmix.X
 * живут рядом с osmix.X. Явная переменная окружения — приоритетнее.
 */
export const storefrontOrigin = (currentOrigin: string, explicit?: string | null): string => {
  if (explicit) return explicit.replace(/\/$/, "")
  return currentOrigin.replace(/^(https?:\/\/)(seller|admin)\./, "$1")
}
